const DEFAULT_PUBLIC_FIELDS = ['application', 'demo', 'introduce']
const publicAssetKeyPattern =
  /(url|src|image|img|cover|banner|avatar|thumb|background|example|cut|video|media)/i
const structuralAssetKeyPattern = /^(\d+|left|right|before|after)$/i
const absoluteAssetPattern = /^(https?:)?\/\//i
const specialAssetPattern = /^(data|blob):/i
const imagePathPattern = /\.(png|jpe?g|webp|gif|svg|bmp|avif)(\?.*)?$/i
const publicAssetPathPattern =
  /(?:^|\/)api\/standard\/algorithm\/[^/]+\/public\/(.+)$/i
const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '')

const stringifyAssetValue = (value) => {
  if (typeof value === 'string') return value
  if (value?.href) return value.href
  return ''
}

const shouldPrefixPublicAsset = (value, key) => {
  const assetValue = stringifyAssetValue(value)
  if (!assetValue.trim()) return false
  if (
    absoluteAssetPattern.test(assetValue) ||
    specialAssetPattern.test(assetValue)
  ) {
    return false
  }
  return publicAssetKeyPattern.test(key) || imagePathPattern.test(assetValue)
}

const normalizePublicAssetPath = (value) => {
  const assetPath = stringifyAssetValue(value)
    .trim()
    .replace(/^\/+/, '')
    .replace(/^public\//i, '')
  const publicAssetMatch = assetPath.match(publicAssetPathPattern)

  // API data may already contain a public asset path. Keep only its file path
  // so normalization always produces one, valid public prefix.
  return publicAssetMatch ? publicAssetMatch[1] : assetPath
}

export const resolveApiAssetUrl = (value) => {
  const assetValue = stringifyAssetValue(value)
  if (!assetValue.trim()) return ''
  const trimmedValue = assetValue.trim()

  if (
    absoluteAssetPattern.test(trimmedValue) ||
    specialAssetPattern.test(trimmedValue)
  ) {
    return trimmedValue
  }

  return `${apiBaseUrl}${
    trimmedValue.startsWith('/') ? '' : '/'
  }${trimmedValue}`
}

const prefixPublicAssetUrls = (
  value,
  prefix,
  key = '',
  inheritedAssetKey = '',
) => {
  const assetKey = publicAssetKeyPattern.test(key)
    ? key
    : inheritedAssetKey

  if (Array.isArray(value)) {
    return value.map((item) =>
      prefixPublicAssetUrls(item, prefix, key, assetKey),
    )
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([childKey, childValue]) => [
        childKey,
        prefixPublicAssetUrls(
          childValue,
          prefix,
          childKey,
          assetKey ||
            (publicAssetKeyPattern.test(childKey) ? childKey : ''),
        ),
      ]),
    )
  }

  if (
    shouldPrefixPublicAsset(value, key) ||
    (assetKey && structuralAssetKeyPattern.test(key))
  ) {
    return `${prefix}${normalizePublicAssetPath(value)}`
  }

  return value
}

export const normalizeAlgorithmPublicAssetUrls = (
  detail,
  id,
  fields = DEFAULT_PUBLIC_FIELDS,
) => {
  if (!detail || !id) return detail || {}
  const prefix = resolveApiAssetUrl(`/api/standard/algorithm/${id}/public/`)
  const normalizedDetail = { ...detail }

  fields.forEach((field) => {
    if (normalizedDetail[field]) {
      normalizedDetail[field] = prefixPublicAssetUrls(
        normalizedDetail[field],
        prefix,
        field,
      )
    }
  })

  return normalizedDetail
}
